import React, { useState, useEffect } from "react";
import { Tabs, Alert } from "antd";
import RolesPageHeader from "../components/rolesAndPermissions/RolesPageHeader";
import RoleHeader from "../components/rolesAndPermissions/RoleHeader";
import PermissionGroup from "../components/rolesAndPermissions/PermissionGroup";
import CreateRoleModal from "../components/rolesAndPermissions/CreateRoleModal";
import PermissionsList from "../components/rolesAndPermissions/PermissionsList";
import { useRoles } from "../hooks/usePermissions";
import { usePermissions } from "../context/PermissionContext";
import { PERMISSIONS } from "../utils/permissions";
import { useNavigate } from "react-router-dom";

const { TabPane } = Tabs;

const RolesAndPermissionsPage: React.FC = () => {
  const { data: roles } = useRoles();
  const [isModalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<{
    id: number;
    name: string;
    description: string;
  } | null>(null);
  const navigate = useNavigate();

  // Check if in development environment
  const isDevelopment = process.env.NODE_ENV === "development";

  // Get user permissions
  const { hasPermission } = usePermissions();
  const canViewRoles = hasPermission(PERMISSIONS.VIEW_ROLES);
  const canCreateRoles = hasPermission(PERMISSIONS.CREATE_ROLES);
  const canModifyRoles = hasPermission(PERMISSIONS.MODIFY_ROLES);
  const canDeleteRoles = hasPermission(PERMISSIONS.DELETE_ROLES);

  // Redirect if user doesn't have permission to view roles
  useEffect(() => {
    if (!canViewRoles) {
      // Redirect to unauthorized page with a delay to prevent render issues
      const timeout = setTimeout(() => {
        navigate("/unauthorized");
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [canViewRoles, navigate]);

  const handleEditRole = (role: {
    id: number;
    name: string;
    description: string;
  }) => {
    if (!canModifyRoles) {
      return; // Don't open modal if user doesn't have permission
    }
    setEditingRole(role);
    setModalVisible(true);
  };

  const handleCreateRole = () => {
    if (!canCreateRoles) {
      return; // Don't open modal if user doesn't have permission
    }
    setModalVisible(true);
  };

  // If user doesn't have permission to view roles, show access denied message
  if (!canViewRoles) {
    return (
      <div className="p-6">
        <Alert
          message="Access Denied"
          description="You don't have permission to access roles and permissions management. Please contact your administrator if you need access."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      {/* Page Header */}
      <RolesPageHeader
        onCreateRole={handleCreateRole}
        canCreateRoles={canCreateRoles}
      />

      <Tabs defaultActiveKey="roles" className="mt-4">
        <TabPane tab="Roles" key="roles">
          {/* Role Sections */}
          {roles?.map((role) => (
            <div
              key={role.id}
              className="mb-8 border border-gray-300 rounded-lg shadow-sm"
            >
              {/* Role Header with Edit & Delete */}
              <RoleHeader
                roleId={role.id}
                roleName={role.name}
                description={role.description}
                color="blue"
                onEdit={() => handleEditRole(role)}
                canModifyRoles={canModifyRoles}
                canDeleteRoles={canDeleteRoles}
              />

              {/* Pass roleId to PermissionGroup */}
              <PermissionGroup roleId={role.id} />
            </div>
          ))}
        </TabPane>

        {/* Only show Permissions tab in development environment */}
        {isDevelopment && (
          <TabPane tab="Permissions" key="permissions">
            <PermissionsList canCreatePermissions={canCreateRoles} />
          </TabPane>
        )}
      </Tabs>

      {/* Create & Edit Role Modal */}
      <CreateRoleModal
        visible={isModalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRole(null);
        }}
        roleToEdit={editingRole}
      />
    </div>
  );
};

export default RolesAndPermissionsPage;
